import { storage } from '@/features/storage';
import { getCookieValue, setCookie } from '@/util/cookies';
import { MILLISECONDS_IN_YEAR } from '@/util/date';
import { clearDemoOrgAccessCookie } from '@/util/demoOrgSession';
import { isImpersonating as checkImpersonating, clearImpersonatingCookie, IMPERSONATING_COOKIE } from '@/util/impersonation';

const ORIGINAL_AUTH_COOKIE_PREFIX = 'original-';

/** Removes client-side auth cookie backups created when impersonation started. */
export const clearStoredOriginalAuthCookies = (): void => {
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith(ORIGINAL_AUTH_COOKIE_PREFIX)) {
      storage.removeItem(key);
    }
  }
};

/**
 * Restores admin auth cookies from localStorage for legacy `impersonating=true` sessions
 * started before server-side exit was deployed.
 */
const restoreLegacyOriginalAuthCookies = (): boolean => {
  const originalAuthCookies = Object.entries(localStorage).filter(([key]) => key.startsWith(ORIGINAL_AUTH_COOKIE_PREFIX));
  if (originalAuthCookies.length === 0) {
    return false;
  }

  for (const [key, value] of originalAuthCookies) {
    setCookie(key.replace(ORIGINAL_AUTH_COOKIE_PREFIX, ''), value, { expires: new Date(Date.now() + MILLISECONDS_IN_YEAR) });
    storage.removeItem(key);
  }

  clearImpersonatingCookie();
  return true;
};

export type ExitImpersonationResult = {
  restored: boolean;
  /** Entry page — only set on the server-exit path. */
  returnUrl?: string;
};

/** Restore admin session via exit-impersonation; `restored` is false if not impersonating or the call fails. */
export const exitImpersonation = async (): Promise<ExitImpersonationResult> => {
  const impersonatingCookie = getCookieValue(IMPERSONATING_COOKIE);

  if (impersonatingCookie === 'true') {
    return { restored: restoreLegacyOriginalAuthCookies() };
  }

  if (!checkImpersonating(impersonatingCookie)) {
    return { restored: false };
  }

  const response = await fetch('/api/admin/users/exit-impersonation', {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as { sessionCleared?: boolean };
    // Only when sessionCleared: a mid-exit 5xx leaves the session swapped; clearing would hide
    // active impersonation and drop getImpersonatorId() attribution.
    if (data.sessionCleared) {
      clearImpersonatingCookie();
      clearDemoOrgAccessCookie();
    }
    return { restored: false };
  }

  clearStoredOriginalAuthCookies();
  const data = (await response.json().catch(() => ({}))) as { returnUrl?: string | null; signedOut?: boolean };
  // Expired demo: server signed out the temp user; admin was not restored.
  if (data.signedOut) return { restored: false };
  return { restored: true, returnUrl: data.returnUrl ?? undefined };
};
